const CHUNK_RELOAD_KEY = 'portal:chunk-reload'
const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk [\d\w-]+ failed/i,
  /CSS_CHUNK_LOAD_FAILED/i,
  /Failed to fetch dynamically imported module/i,
]

function getErrorMessage(error) {
  if (!error) {
    return ''
  }

  if (typeof error === 'string') {
    return error
  }

  if (error.reason) {
    return getErrorMessage(error.reason)
  }

  return [error.name, error.message, error.stack].filter(Boolean).join(' ')
}

function isChunkLoadError(error) {
  const message = getErrorMessage(error)
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message))
}

function getReloadState() {
  const raw = sessionStorage.getItem(CHUNK_RELOAD_KEY)

  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw)
  } catch (error) {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
    return null
  }
}

function clearReloadState() {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY)
}

function getAppBasePath() {
  const baseUrl = process.env.BASE_URL || '/'
  const normalizedBase = baseUrl.startsWith('/') ? baseUrl : `/${baseUrl}`
  return normalizedBase.endsWith('/') ? normalizedBase : `${normalizedBase}/`
}

function getCurrentTargetPath() {
  return `${window.location.pathname}${window.location.search}` || '/'
}

function buildBrowserPath(targetPath) {
  const basePath = getAppBasePath()
  const normalizedPath = targetPath.startsWith('/') ? targetPath : `/${targetPath}`

  if (basePath !== '/' && !normalizedPath.startsWith(basePath)) {
    return `${basePath.replace(/\/$/, '')}${normalizedPath}`
  }

  return normalizedPath
}

function normalizeTargetPath(targetPath) {
  if (!targetPath) {
    return getCurrentTargetPath()
  }

  if (targetPath.startsWith('#')) {
    return targetPath.replace(/^#/, '') || '/'
  }

  return targetPath.startsWith('/') ? targetPath : `/${targetPath}`
}

function hasRecentReloadForPath(targetPath) {
  const state = getReloadState()

  if (!state || state.path !== targetPath) {
    return false
  }

  return Date.now() - state.time < 15000
}

function persistReloadState(targetPath) {
  sessionStorage.setItem(
    CHUNK_RELOAD_KEY,
    JSON.stringify({
      path: targetPath,
      time: Date.now()
    })
  )
}

function isAsyncAssetLoadEvent(event) {
  const assetUrl = event?.target?.src || event?.target?.href
  const tagName = event?.target?.tagName?.toUpperCase()

  if (!assetUrl || !tagName) {
    return false
  }

  return ['SCRIPT', 'LINK'].includes(tagName) && /\.(js|css)(\?|$)/i.test(assetUrl)
}

function reloadCurrentPage(targetPath) {
  const normalizedPath = normalizeTargetPath(targetPath)

  if (hasRecentReloadForPath(normalizedPath)) {
    clearReloadState()
    return false
  }

  persistReloadState(normalizedPath)

  const nextUrl = new URL(window.location.href)
  const browserPath = buildBrowserPath(normalizedPath)
  nextUrl.pathname = browserPath.split('?')[0] || '/'
  nextUrl.search = browserPath.includes('?') ? browserPath.slice(browserPath.indexOf('?')) : ''
  nextUrl.hash = ''
  window.location.replace(nextUrl.toString())
  window.location.reload()
  return true
}

function tryRecoverFromChunkError(error, targetPath) {
  if (!isChunkLoadError(error)) {
    return false
  }

  return reloadCurrentPage(targetPath)
}

export function installChunkLoadErrorHandler(router) {
  if (typeof window === 'undefined') {
    return
  }

  router.onError((error, to) => {
    const recovered = tryRecoverFromChunkError(error, to?.fullPath)

    if (!recovered && isChunkLoadError(error)) {
      console.error('Chunk load failed after retry, manual refresh may be required.', error)
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (tryRecoverFromChunkError(event.reason)) {
      event.preventDefault()
    }
  })

  window.addEventListener('error', (event) => {
    if (isAsyncAssetLoadEvent(event)) {
      const targetPath = getCurrentTargetPath()
      const recovered = reloadCurrentPage(targetPath)

      if (recovered) {
        event.preventDefault()
      }
    }
  }, true)

  router.afterEach(() => {
    clearReloadState()
  })
}
